import { useEffect, useMemo } from "react";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { PulseLoader } from "react-spinners";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";

// Map i18n lang -> Day.js locale
const LOCALE_MAP = {
  en: "en",
  "en-gb": "en-gb",
  "en-GB": "en-gb",
  fr: "fr",
  de: "de",
  es: "es",
  it: "it",
  nl: "nl",
  pl: "pl",
  pt: "pt",
  sv: "sv",
  ar: "ar",
  tr: "tr",
};

// Explicit locale loaders (avoid *.d.ts import issues)
const DAYJS_LOCALE_LOADERS = {
  en:    () => import("dayjs/locale/en"),
  "en-gb": () => import("dayjs/locale/en-gb"),
  fr:    () => import("dayjs/locale/fr"),
  de:    () => import("dayjs/locale/de"),
  es:    () => import("dayjs/locale/es"),
  it:    () => import("dayjs/locale/it"),
  nl:    () => import("dayjs/locale/nl"),
  pl:    () => import("dayjs/locale/pl"),
  pt:    () => import("dayjs/locale/pt"),
  sv:    () => import("dayjs/locale/sv"),
  ar:    () => import("dayjs/locale/ar"),
  tr:    () => import("dayjs/locale/tr"),
};

const normalizeToDayjsLocale = (lang) => {
  if (!lang) return "en";
  const lower = lang.toLowerCase();
  const primary = lower.split("-")[0];
  return LOCALE_MAP[lower] || LOCALE_MAP[primary] || "en";
};

const StepThree = (props) => {
  const { i18n, t } = useTranslation("makeBooking");

  const days = props.barberWorkingDays;
  const today = dayjs();
  const todayString = `${today.$D}-${today.$M}-${today.$y}`;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (props.defaultDate === undefined || props.defaultDate === null) {
      if (!props.barberWorkingDays.includes(0)) {
        props.queryDate(dayjs().add(0, "day"));
      } else {
        props.queryDate(dayjs().add(1, "day"));
      }
    } else {
      props.queryDate(props.defaultDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve and load Day.js locale; also pass to MUI adapter
  const dayjsLocale = useMemo(
    () => normalizeToDayjsLocale(i18n.language),
    [i18n.language]
  );

  useEffect(() => {
    const loader = DAYJS_LOCALE_LOADERS[dayjsLocale];
    (loader ? loader() : Promise.resolve()).finally(() => {
      dayjs.locale(dayjsLocale);
    });
  }, [dayjsLocale]);

  return (
    <div style={{ marginTop: "2rem" }} className="booking-portal-step-wrapper">
      <h2
        style={{ marginBottom: 0, marginLeft: "auto", marginRight: "auto" }}
        className="grid-title"
      >
        {t("stepThree.titles.selectTime")}
      </h2>

      <section>
        <Container>
          {days === null ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <div style={{ marginTop: "2rem" }}>
                <PulseLoader
                  color={"#333"}
                  loading={days === null}
                  size={25}
                  aria-label={t("stepThree.status.loadingAria")}
                  data-testid="loader"
                />
              </div>
            </div>
          ) : (
            <Row>
              <Col xxl={12}>
                <IconButton aria-label={t("stepThree.actions.back")} size="large">
                  <ArrowBackIcon
                    className="back-arrow"
                    onClick={() => {
                      props.goTo(1);
                      props.setAvailableTime("waiting");
                    }}
                  />
                </IconButton>
              </Col>

              <Col className="mb-3" xxl={4} xl={4} lg={5} md={6} sm={12}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={dayjsLocale}
                >
                  <StaticDatePicker
                    defaultValue={
                      props.defaultDate === undefined
                        ? dayjs().add(props.barberWorkingDays.includes(0) ? 1 : 0, "day")
                        : props.defaultDate == null
                        ? dayjs().add(props.barberWorkingDays.includes(0) ? 1 : 0, "day")
                        : props.defaultDate
                    }
                    disablePast
                    disableHighlightToday
                    maxDate={dayjs().add(2, "months")}
                    componentsProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
                    onChange={(date) => {
                      props.queryDate(date);
                    }}
                    shouldDisableDate={(date) => {
                      // Use native Date for a stable weekday (0=Sun..6=Sat) independent of locale
                      const jsDow = date.toDate().getDay();

                      if (todayString === `${date.$D}-${date.$M}-${date.$y}` && days.includes(0)) {
                        return true;
                      }
                      // your existing mapping: data uses 1..7 -> 0..6 via (day - 1)
                      const isWorkingDay = days.some((d) => d - 1 === jsDow);
                      return !isWorkingDay;
                    }}
                  />
                </LocalizationProvider>
              </Col>

              <Col xxl={8} xl={8} lg={7} md={6} sm={12} className="time-value">
                <Row>
                  {props.availableTimes === "waiting" ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <div className="test">
                        <PulseLoader
                          color={"#333"}
                          loading={props.availableTimes === "waiting"}
                          size={12}
                          aria-label={t("stepThree.status.loadingAria")}
                          data-testid="loader"
                        />
                      </div>
                    </div>
                  ) : props.availableTimes === null ? (
                    <div className="service-info">
                      <p>{t("stepThree.status.noTimes")}</p>
                    </div>
                  ) : (
                    props.availableTimes.map((availableTime, index) => (
                      <Col className="mb-3" key={index} lg={3} md={4} sm={3} xs={4}>
                        <div
                          style={{ marginLeft: "auto", marginRight: "auto" }}
                          className="time-card"
                          onClick={() => {
                            props.goTo(3);
                            props.selectTime(availableTime.label);
                            props.selectTimeId(availableTime.value);
                          }}
                          role="button"
                          aria-label={t("stepThree.actions.selectTimeSlot", {
                            time: availableTime.label,
                          })}
                        >
                          {availableTime.label}
                        </div>
                      </Col>
                    ))
                  )}
                </Row>
              </Col>
            </Row>
          )}
        </Container>
      </section>
    </div>
  );
};

export default StepThree;
